/**
 * Organization Sports Service
 * Frontend API service for managing organization-specific sports
 */

import axios from "axios";

// Create axios instance with credentials for cookie-based auth
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:5000/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true, // Enable cookies
});

const organizationSportsService = {
  /**
   * Get sports available to the user's organization
   */
  async getOrganizationSports(includeUnavailable = false) {
    try {
      const response = await api.get("/organization/sports", {
        params: { includeUnavailable },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch organization sports:", error);
      throw error;
    }
  },

  /**
   * Add a sport to the organization (Admin only)
   */
  async addSportToOrganization(sportId, facilityData = {}) {
    try {
      const response = await api.post("/organization/sports", {
        sportId,
        ...facilityData,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to add sport to organization:", error);
      throw error;
    }
  },

  /**
   * Remove sport from organization (Admin only)
   */
  async removeSportFromOrganization(sportId) {
    try {
      const response = await api.delete(`/organization/sports/${sportId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to remove sport from organization:", error);
      throw error;
    }
  },

  /**
   * Update organization sport settings (Admin only)
   */
  async updateOrganizationSport(sportId, updateData) {
    try {
      const response = await api.put(
        `/organization/sports/${sportId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update organization sport:", error);
      throw error;
    }
  },

  /**
   * Get all sports available on platform (for adding to organization)
   */
  async getAllAvailableSports() {
    try {
      const response = await api.get("/organization/sports/available");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch available sports:", error);
      throw error;
    }
  },

  /**
   * Get organizations that offer a specific sport
   */
  async getOrganizationsBySport(sportId) {
    try {
      const response = await api.get(
        `/organization/sports/by-sport/${sportId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch organizations by sport:", error);
      throw error;
    }
  },

  /**
   * Get organization sports statistics (Admin only)
   */
  async getOrganizationSportsStats() {
    try {
      const response = await api.get("/organization/sports/stats");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch organization sports stats:", error);
      throw error;
    }
  },
};

export default organizationSportsService;
